import { FC, Fragment, useCallback, useMemo } from 'react';
import {
    AnvilSelect,
    AnvilSelectOptionsProps,
    BodyText,
    Button,
    Icon,
    Stack,
    useScreenSize,
} from '@servicetitan/design-system';
import * as Styles from './pagination.module.less';
import classNames from 'classnames';

const iconBlueColor = '#0265DC';

const qaTag = (name: string) => `pdf-viewer-pagination-${name}`;

export interface PaginationProps {
    totalPages: number;
    currentPage: number;
    onJumpToPage: (page: number) => void;
}

const trigger = {
    clearable: false,
    size: 'small',
    className: classNames(Styles.paginationPage, `qa-${qaTag('select')}`),
};
const valueIfTrue = (condition: boolean, value: string) => (condition ? value : undefined);

export const Pagination: FC<PaginationProps> = props => {
    const { totalPages, currentPage, onJumpToPage } = props;
    const screenSize = useScreenSize();

    const isMobile = screenSize.smallerThan.mobile;

    const options = useMemo(() => {
        return Array.from(
            { length: totalPages },
            (_, i) =>
                ({
                    text: `${i + 1}`,
                    value: i + 1,
                    selected: i + 1 === currentPage,
                }) as AnvilSelectOptionsProps
        );
    }, [currentPage, totalPages]);

    const handleCurrentPageChange = useCallback(
        (anvilProps: AnvilSelectOptionsProps | AnvilSelectOptionsProps[]) => {
            onJumpToPage((anvilProps as AnvilSelectOptionsProps).value);
        },
        [onJumpToPage]
    );

    const prevDisabled = currentPage === 1;
    const nextDisabled = currentPage === totalPages;

    return (
        <Stack alignItems="center" direction="row" spacing={1}>
            {!isMobile && (
                <Fragment>
                    <Button
                        fill="subtle"
                        small
                        disabled={prevDisabled}
                        icon={
                            <Icon
                                color={valueIfTrue(!prevDisabled, iconBlueColor)}
                                name="skip_previous"
                            />
                        }
                        onClick={() => onJumpToPage(1)}
                        data-qa={qaTag('start')}
                    />
                    <Button
                        fill="subtle"
                        small
                        disabled={prevDisabled}
                        icon={
                            <Icon
                                color={valueIfTrue(!prevDisabled, iconBlueColor)}
                                className={Styles.paginationPreviousPageIcon}
                                name="play_arrow"
                            />
                        }
                        onClick={() => onJumpToPage(currentPage - 1)}
                        data-qa={qaTag('previous')}
                    />
                </Fragment>
            )}
            <Stack direction="row" spacing={1} alignItems="center">
                <AnvilSelect
                    trigger={trigger}
                    options={options}
                    onChange={handleCurrentPageChange}
                    value={currentPage}
                    scrollHeight="350px"
                />
                <BodyText className="flex-shrink-0" size="small" data-qa={qaTag('total-pages')}>
                    of {totalPages}
                </BodyText>
            </Stack>
            {!isMobile && (
                <Fragment>
                    <Button
                        fill="subtle"
                        small
                        disabled={nextDisabled}
                        icon={
                            <Icon
                                color={valueIfTrue(!nextDisabled, iconBlueColor)}
                                name="play_arrow"
                            />
                        }
                        onClick={() => onJumpToPage(currentPage + 1)}
                        data-qa={qaTag('next')}
                    />
                    <Button
                        fill="subtle"
                        small
                        disabled={nextDisabled}
                        icon={
                            <Icon
                                color={valueIfTrue(!nextDisabled, iconBlueColor)}
                                name="skip_next"
                            />
                        }
                        data-qa={qaTag('end')}
                        onClick={() => onJumpToPage(totalPages)}
                    />
                </Fragment>
            )}
        </Stack>
    );
};
